import Link from "next/link";

import { routes } from "src/shared/routes";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

const PLATFORM_TERMS_URL = "https://www.attend.tech/terms-of-use";
const PLATFORM_PRIVACY_URL = "https://www.attend.tech/privacy-policy";
const ACCOUNT_MANAGER_URL =
  "https://auth.ticketmaster.com/as/authorization.oauth2?redirect_uri=https%3A%2F%2Fam.ticketmaster.com%2Ffalcons%2Fam-sso%3Fdeeplink%3DL2ZhbGNvbnMvZW4vbXktZXZlbnRz&response_type=code&lang=en-us&integratorId=NAM&placementId=homepage&visualPresets=falcons&hideLeftPanel=false&client_id=4f3cd0fe7805.web.falcons-falcons.us&scope=openid%20profile%20phone%20email%20tm";
const MOBILE_TICKETING_GUIDE_URL =
  "https://www.atlantafalcons.com/mobile-ticketing-guide/";
const SINGLE_GAME_TICKETS_URL = "https://www.ticketmaster.com/artist/805897";

// Flight Pass FAQ — copy provided by the Falcons.
export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Who is eligible to purchase the Falcons Flight Pass?",
    answer:
      "Falcons Flight Pass is designed for young adults to experience what it is like to be a part of the Falcons Community and attend game days at Mercedes-Benz Stadium.",
  },
  {
    question: "Which games are included with my Flight Pass?",
    answer:
      "Passes include a Standing Room Only ticket to all regular season home games.",
  },
  {
    question: "Where can I go with a Standing Room Ticket?",
    answer:
      "While there isn't a designated spot in the stadium, most fans find the 200 and 300-level Skybridges to be a great area to enjoy the action on the field.",
  },
  {
    question:
      "How do I claim my tickets? When does the check-in open before each game?",
    answer: (
      <>
        You must confirm your attendance prior to each game. You will receive a
        text at 10:00 AM on Wednesday before a gameday, allowing you to check in
        for the upcoming game. You will need to log in to your Flight Pass
        account to mark your attendance for each game.
        <br />
        <br />
        If you check in, you&apos;ll receive a text with your ticket link four
        hours before kickoff.
      </>
    ),
  },
  {
    question: "Can I purchase multiple Flight Passes?",
    answer:
      "Yes, up to two (2) passes per account. You can also buy up to four guest tickets per game during the check-in window (if available). Tickets are non-transferable and non-sellable, so all guests will need to enter with you. Violators will be removed from the program.",
  },
  {
    question: "Can I upgrade my seat?",
    answer:
      "Seat upgrades may be available when you check in for a game day but are on a game-by-game basis. If you upgrade a game, the change applies to all pass seats that are in your account for that game.",
  },
  {
    question: "What if I lose the text message with my pass?",
    answer: (
      <>
        Log into your{" "}
        <Link target="_blank" href={ACCOUNT_MANAGER_URL}>
          Atlanta Falcons Account Manager
        </Link>{" "}
        to access your tickets.
      </>
    ),
  },
  {
    question: "What happens if I don't check in for a game?",
    answer:
      "There are no penalties, but you will not receive a ticket unless you check in. If you miss the check-in window, you may activate your pass up to one hour after kick-off, subject to availability. Guest passes and upgrades will not be available after the check-in window closes.",
  },
  {
    question: "How do I access my tickets for entry to Mercedes-Benz Stadium?",
    answer: (
      <>
        Log in to the{" "}
        <Link target="_blank" href={ACCOUNT_MANAGER_URL}>
          Atlanta Falcons Account Manager
        </Link>
        <br />
        <br />
        Please visit the{" "}
        <Link target="_blank" href={MOBILE_TICKETING_GUIDE_URL}>
          Atlanta Falcons Mobile Ticketing Guide
        </Link>{" "}
        for assistance on accessing your tickets in your Account Manager via the
        Atlanta Falcons App.
      </>
    ),
  },
  {
    question: "Can I sell, transfer, or get a refund for my Flight Pass?",
    answer:
      "No, Flight Passes are non-transferable, non-refundable, and cannot be resold. Violators will be removed from the program.",
  },
  {
    question: "What other benefits are included?",
    answer: "The Flight Pass does not provide membership benefits.",
  },
  {
    question: "Does the Flight Pass include post-season games or other events?",
    answer: "No, the pass is valid for regular season home games only.",
  },
  {
    question: "Can I purchase the pass if I don't have a smartphone?",
    answer:
      "No, the Flight Pass is mobile-only. No printed tickets will be issued.",
  },
  {
    question: "How is this different than a regular Season Ticket?",
    answer:
      "We do not have standing room only season tickets. Falcons' season ticket membership includes a number of other benefits that do not come with the Flight Pass.",
  },
  {
    question:
      "Can I buy tickets for individual games instead of the full season?",
    answer: (
      <>
        The Flight Pass provides standing room only access to all regular season
        home games for a one-time fee. If you need additional single game
        tickets or would prefer to buy on an individual game basis, please visit{" "}
        <Link target="_blank" href={SINGLE_GAME_TICKETS_URL}>
          2026-27 Atlanta Falcons Tickets
        </Link>
        .
      </>
    ),
  },
];

// New User Registration terms (auth page opt-in)
export const REGISTRATION_TERMS_AND_CONDITION = () => (
  <>
    By creating an account, you agree to receive account-related notification
    and marketing communication from the Atlanta Falcons and you confirm that
    you have read and agree to the platform&apos;s{" "}
    <Link target="_blank" href={PLATFORM_TERMS_URL}>
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={PLATFORM_PRIVACY_URL}>
      Privacy Policy
    </Link>
    .
  </>
);

// Checkout Purchase terms
export const TERMS_AND_CONDITION = () => (
  <>
    By checking the box you agree to receive notifications regarding your
    purchase and to the{" "}
    <Link target="_blank" href={PLATFORM_TERMS_URL}>
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href={PLATFORM_PRIVACY_URL}>
      Privacy Policy
    </Link>
    .
  </>
);

export const PASS_PURCHASE_SUCCESS_MESSAGE: TTeamConfig["LANGUAGES"]["PASS_PURCHASE_SUCCESS_MESSAGE"] =
  {
    TITLE: () => (
      <>
        You&apos;re all set to experience the action! Don&apos;t forget to
        confirm attendance here on game day!
      </>
    ),
    DESCRIPTION: () => {
      const router = useRouterExt();
      return (
        <>
          Friends don&apos;t leave friends behind.{" "}
          <b>
            <u>
              <span
                onClick={() => router.pushSchema(routes.my_group)}
                style={{ cursor: "pointer" }}
              >
                Invite your friends
              </span>
            </u>
          </b>{" "}
          and enjoy games together.
        </>
      );
    },
  };
